import { FC, ReactNode } from 'react'

import { Select as AntdSelect, SelectProps as AntdSelectProps } from 'antd'
import styled, { createGlobalStyle, css } from 'styled-components'

import DownArrow from 'src/Assets/Svg/common/down-arrow.svg?react'
import ArrowLeftIcon from 'src/Assets/Svg/common/check-mark.svg?react'
import { variables } from 'src/Styled/variables'

const dropdownClassName = 'custom-select-dropdown'
const sidebarDropdownClassName = 'sidebar-select-dropdown'

const GlobalStyled = createGlobalStyle<{ $isDisabled?: boolean }>`
  .${dropdownClassName} {
    margin-top: 15px !important;
    background-color: ${({ theme }) => theme.color.white} !important;
    border: ${({ theme }) => `1px solid ${theme.color.lightGrey}`};
    border-radius: 16px;
    padding: 8px !important;

    .ant-select-item {
      border-bottom: none;
    }

    .ant-select-item-option-content {
      color: ${({ theme }) => theme.color.darkGrey};
      font-family: 'Segoe UI';
      line-height: 150%;
      font-size: ${variables.fontSize14};
      font-weight: ${variables.fontWeight400};
    }

    .ant-select-item-option-selected {
      position: relative;
      padding: 10px !important;
      background-color: unset !important;

      &:hover {
        background-color: unset !important;
      }
    }

    .ant-select-item-option {
      padding: 10px !important;
      border: ${() => '1px solid transparent'};
      border-radius: 8px;
      &:hover {
        background-color: unset;
        border: ${({ theme }) => `1px solid ${theme.color.lightGrey}`};
      }
    }

    .ant-select-item-option-active{
      background-color: unset !important;
    }

    .rc-virtual-list-scrollbar-thumb {
      background-color: ${({ theme }) => theme.color.mediumGrey} !important;
    }

    &::before {
      display: none !important;
    }

    .ant-empty-description {
      color: ${({ theme }) => theme.color.darkGrey};
    }
  }
`

const SidebarGlobalStyled = createGlobalStyle`
  .${sidebarDropdownClassName} {
    background-color: ${({ theme }) => theme.sidebarColor.blackGrey} !important;
    border: ${({ theme }) => `1px solid ${theme.sidebarColor.darkGrey}`};
    border-radius: 16px;
    padding: 8px !important;
    left: 24px !important;
    right: 24px !important;
    width: auto !important;
    box-shadow: 0 0 10px rgba(52, 52, 52, 0.3);

    .rc-virtual-list-holder-inner {
      gap: 8px;
    }

    .ant-select-item {
      display: flex;
      align-items: center;
      min-height: 40px;
      border-bottom: none;
      padding: 0 16px !important;
      border: 1px solid transparent;
      border-radius: 12px;
      background-color: transparent !important;
    }

    .ant-select-item-option-content {
      color: ${({ theme }) => theme.sidebarColor.white};
      font-family: 'Segoe UI';
      line-height: 150%;
      font-size: ${variables.fontSize14};
      font-weight: ${variables.fontWeight400};
    }

    .ant-select-item-option-active,
    .ant-select-item-option:hover {
      background-color: ${({ theme }) => theme.sidebarColor.hoverGrey} !important;
      border-color: ${({ theme }) => theme.sidebarColor.darkGrey};
    }

    .rc-virtual-list-scrollbar-thumb {
      background-color: ${({ theme }) => theme.sidebarColor.grey} !important;
    }

    &::before {
      display: none !important;
    }

    .ant-empty-description {
      color: ${({ theme }) => theme.sidebarColor.white};
    }
  }
`

const Container = styled.div<{ $isSidebar?: boolean }>`
  width: 100%;
  position: relative;

  &:focus-within p {
    font-size: ${variables.fontSize12};
    opacity: 1;
    top: 7px;
    left: 16px;
    transition: all 0.2s ease;
    margin: 0;
    z-index: 2;
  }

  &:has(.ant-select-focused) {
    p {
      font-size: ${variables.fontSize12};
      opacity: 1;
      top: 7px;
      left: 16px;
      transition: all 0.2s ease;
      margin: 0;
      z-index: 2;
    }
  }

  &:has(.ant-select-selection-item) {
    p {
      font-size: ${variables.fontSize12};
      opacity: 1;
      top: 7px;
      left: 16px;
      transition: all 0.2s ease;
      margin: 0;
      z-index: 2;
    }
  }

  ${({ $isSidebar }) =>
    $isSidebar &&
    css`
      width: auto;
      display: flex;
      justify-content: center;
    `}
`

const Label = styled.p<{
  $disabled?: boolean
}>`
  font-family: 'Segoe UI';
  color: ${({ theme, $disabled }) =>
    $disabled ? theme.color.lightGrey : theme.color.slateGrey} !important;

  font-size: ${variables.fontSize14};
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
  opacity: 0;
  transition: all 0.2s ease;
  position: absolute;
  top: 27px;
  left: 18px;
  z-index: 0;
  pointer-events: none;
`

const sidebarSelectStyles = css`
  &.ant-select {
    height: auto !important;
  }

  .ant-select-selector {
    padding: 0 !important;
    border: none !important;
    background-color: transparent !important;
    box-shadow: none !important;
    height: auto !important;

    &:hover,
    &:focus-within,
    &:active {
      background-color: transparent !important;
      border: none !important;
    }
  }

  &.ant-select-open .ant-select-selector {
    background-color: transparent !important;
  }

  .ant-select-selection-item {
    padding-top: 0 !important;
    padding-right: 20px !important;
    color: ${({ theme }) => theme.sidebarColor.white} !important;
    font-family: 'Segoe UI';
    font-size: ${variables.fontSize14};
    font-weight: ${variables.fontWeight400};
    line-height: 150%;
    text-align: center;
  }

  .ant-select-arrow {
    right: 0 !important;
    svg path {
      fill: ${({ theme }) => theme.sidebarColor.white};
    }
  }

  &.ant-select-disabled {
    .ant-select-selector {
      background-color: transparent !important;
    }

    .ant-select-selection-item {
      color: ${({ theme }) => theme.sidebarColor.grey} !important;
    }

    .ant-select-arrow {
      display: none;
    }
  }
`

const StyledSelect = styled(AntdSelect)<{
  $isError?: boolean
  $isWarning?: boolean
  $hasNoPlaceholderOnFocus?: boolean
  $isSidebar?: boolean
}>`
  width: 100%;
  border-radius: 16px;
  height: 56px !important;

  .ant-select-arrow {
    transition: transform 0.3s !important;
    right: 20px !important;
    svg {
      path {
        fill: ${({ theme }) => theme.color.darkGrey};
      }
    }
  }

  &.ant-select-open {
    .ant-select-arrow {
      transform: rotate(180deg) !important;
      transition: transform 0.3s !important;
    }

    .ant-select-selector {
      background-color: ${({ theme }) => theme.color.white} !important;
    }
  }

  .ant-select-outlined {
    outline: none !important;
  }

  .ant-select-selector {
    outline: none !important;
    border-radius: 16px;
    height: 56px !important;
    padding-left: 16px !important;
    padding-right: 16px !important;
    background-color: ${({ theme }) => theme.color.white} !important;
    border: ${({ theme, $isError, $isWarning }) => {
      if ($isError) {
        return `1px solid ${theme.color.mediumRed}`
      }
      if ($isWarning) {
        return `1px solid ${theme.color.mediumOrange}`
      }
      return `1px solid ${theme.color.lightGrey}`
    }} !important;

    &:hover {
      background-color: ${({ theme }) => theme.color.hoverWhite} !important;
      border: ${({ theme, $isError, $isWarning }) => {
        if ($isError) {
          return `1px solid ${theme.color.mediumRed}`
        }
        if ($isWarning) {
          return `1px solid ${theme.color.mediumOrange}`
        }
        return `1px solid ${theme.color.lightGrey}`
      }} !important;
    }
    &:focus-within {
      background-color: ${({ theme }) => theme.color.hoverWhite};
      box-shadow: none !important;
      border: ${({ theme, $isError, $isWarning }) => {
        if ($isError) {
          return `1px solid ${theme.color.mediumRed}`
        }
        if ($isWarning) {
          return `1px solid ${theme.color.mediumOrange}`
        }
        return `1px solid ${theme.color.brandGreen}`
      }} !important;
    }
    &:active {
      background-color: ${({ theme }) => theme.color.white};
    }
  }

  .ant-select-selection-placeholder {
    color: ${({ theme }) => theme.color.slateGrey} !important;
    font-family: 'Segoe UI';
    line-height: 150%;
    font-size: ${variables.fontSize14};
    font-weight: ${variables.fontWeight400};
    opacity: 1;
    transition: opacity 0.2s ease;
  }

  ${({ $hasNoPlaceholderOnFocus }) =>
    $hasNoPlaceholderOnFocus &&
    css`
      &.ant-select-focused .ant-select-selection-placeholder {
        opacity: 0;
      }

      &.ant-select-has-value .ant-select-selection-placeholder {
        opacity: 0;
      }
    `}

  .ant-select-selection-item {
    color: ${({ theme }) => theme.color.darkGrey} !important;
    padding-top: 16px !important;
    padding-left: 0px !important;
  }

  .ant-select-selection-search-input {
    color: ${({ theme }) => theme.color.darkGrey} !important;
    background-color: transparent !important;
    padding-top: 10px !important;
  }

  &.ant-select-disabled {
    .ant-select-selection-placeholder {
      color: ${({ theme }) => theme.color.lightGrey} !important;
    }

    .ant-select-arrow {
      display: none;
    }

    .ant-select-selector {
      background-color: ${({ theme }) => theme.color.white} !important;
    }

    .ant-select-selection-item {
      color: ${({ theme }) => theme.color.lightGrey} !important;
    }
  }

  ${({ $isSidebar }) => $isSidebar && sidebarSelectStyles}
`

const ErrorMessage = styled.div`
  font-family: 'Segoe UI';
  color: ${({ theme }) => theme.color.mediumRed};
  font-size: ${variables.fontSize14};
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
  margin-top: 5px;
`

const WarningMessage = styled(ErrorMessage)`
  color: ${({ theme }) => theme.color.mediumOrange};
`

const ArrowWrapper = styled.div<{ $isSidebar?: boolean }>`
  color: ${({ theme, $isSidebar }) =>
    $isSidebar ? theme.sidebarColor.brandGreen : theme.color.brandGreen};
`

export type SelectAppearance = 'default' | 'sidebar'

export type SelectProps = AntdSelectProps & {
  error?: string
  warning?: string
  label?: string
  hasNoPlaceholderOnFocus?: boolean
  appearance?: SelectAppearance
}

const popupClassNames: Record<SelectAppearance, string> = {
  default: dropdownClassName,
  sidebar: sidebarDropdownClassName,
}

const popupStyles: Record<SelectAppearance, typeof GlobalStyled> = {
  default: GlobalStyled,
  sidebar: SidebarGlobalStyled,
}

const resolvePopupClassName = (
  appearance: SelectAppearance,
  popupClassName?: string,
): string => popupClassName ?? popupClassNames[appearance]

const renderMessages = (
  isSidebar: boolean,
  error?: string,
  warning?: string,
): ReactNode => {
  if (isSidebar) {
    return null
  }

  return (
    <>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {warning && <WarningMessage>{warning}</WarningMessage>}
    </>
  )
}

export const PrimarySelect: FC<SelectProps> = (props) => {
  const {
    error,
    warning,
    label,
    disabled,
    hasNoPlaceholderOnFocus,
    appearance = 'default',
    ...selectProps
  } = props

  const isSidebar = appearance === 'sidebar'
  const showLabel = !isSidebar && label
  const PopupStyles = popupStyles[appearance]

  return (
    <Container $isSidebar={isSidebar}>
      {showLabel && <Label $disabled={disabled}>{label}</Label>}
      <StyledSelect
        menuItemSelectedIcon={
          <ArrowWrapper $isSidebar={isSidebar}>
            <ArrowLeftIcon />
          </ArrowWrapper>
        }
        {...selectProps}
        disabled={disabled}
        popupClassName={resolvePopupClassName(
          appearance,
          selectProps.popupClassName,
        )}
        suffixIcon={selectProps.suffixIcon ?? <DownArrow />}
        $isError={Boolean(error)}
        $isWarning={Boolean(warning)}
        $hasNoPlaceholderOnFocus={hasNoPlaceholderOnFocus}
        $isSidebar={isSidebar}
      />
      {renderMessages(isSidebar, error, warning)}
      <PopupStyles />
    </Container>
  )
}
